// Abbreviates a free-text shirt size from VBS into a short, readable
// 2–9 char code that fits the Roster's Shirt column. Examples:
//   '2T T-shirt'               -> '2T'
//   'Kid T-shirt - Medium'     -> 'Kids M'
//   'Kid T-shirt - X-Small'    -> 'Kids XS'
//   'Adult T-shirt - Large'    -> 'Adult L'
//   'Adult T-shirt - XX-Large' -> 'Adult 2XL'
//
// The recognised prefixes are Kid / Youth / Adult plus the toddler
// 2T-5T family, and the recognised size words run from 'XX-Small'
// through 'XX-Large'. Anything we don't recognise falls through to a
// length-capped pass-through so the column can't be widened by
// surprise data; the original value is still preserved on the
// Attendee doc and surfaced via tooltip in the UI.

// Known VBS shirt size values, ordered smallest-first: toddler T
// sizes, then Kid sizes, then Adult sizes (each block in ascending
// size order). Used to populate the Shirt size dropdown in
// `attendee-form.tsx`. Stored verbatim on the Attendee doc; the Roster
// renders them via `formatShirtSize` (e.g. 'Kid T-shirt - Medium' →
// 'Kids M', '2T T-shirt' → '2T'). If VBS adds a new option, append it
// here so admins can pick it from the dropdown — until then it'll
// surface as a one-off "orphan" value preserved on the doc but not in
// the picker.
export const SHIRT_SIZE_OPTIONS = [
  '2T T-shirt',
  '3T T-shirt',
  '4T T-shirt',
  '5T T-shirt',
  'Kid T-shirt - X-Small',
  'Kid T-shirt - Small',
  'Kid T-shirt - Medium',
  'Kid T-shirt - Large',
  'Kid T-shirt - X-Large',
  'Adult T-shirt - X-Small',
  'Adult T-shirt - Small',
  'Adult T-shirt - Medium',
  'Adult T-shirt - Large',
  'Adult T-shirt - X-Large',
  'Adult T-shirt - XX-Large',
] as const;

const PREFIX_MAP: Record<string, string> = {
  kid: 'Kids',
  youth: 'Youth',
  adult: 'Adult',
};

const SIZE_MAP: Record<string, string> = {
  'xx-small': '2XS',
  'x-small': 'XS',
  small: 'S',
  medium: 'M',
  large: 'L',
  'x-large': 'XL',
  'xx-large': '2XL',
};

// Sorted descending by length so 'xx-large' is matched before 'large',
// 'x-small' before 'small', etc. Computed once at module load.
const SIZE_KEYS_BY_LENGTH = Object.keys(SIZE_MAP).sort(
  (a, b) => b.length - a.length,
);

// Sized to match the widest recognised output ('Adult 2XL' = 9 chars)
// so an unrecognised value can't push the column wider than a normal
// one. Anything longer is truncated to this length.
const MAX_FALLBACK_LEN = 9;

export const formatShirtSize = (raw: string | undefined): string => {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  // Toddler T sizes ('2T T-shirt' through '5T T-shirt') put the size
  // code in the prefix rather than after a ' - ' delimiter, so the
  // Kid/Youth/Adult parse below doesn't apply to them. Match those
  // explicitly and return the bare code (e.g. '2T') — that's already
  // short enough for the column on its own.
  const toddlerMatch = lower.match(/^([2-5]t)\b/);
  if (toddlerMatch) return toddlerMatch[1].toUpperCase();
  let prefix = '';
  for (const key of Object.keys(PREFIX_MAP)) {
    if (lower.startsWith(key)) {
      prefix = PREFIX_MAP[key];
      break;
    }
  }
  let size = '';
  for (const key of SIZE_KEYS_BY_LENGTH) {
    if (lower.endsWith(key)) {
      size = SIZE_MAP[key];
      break;
    }
  }
  if (prefix && size) return `${prefix} ${size}`;
  // Unrecognised format: pass through but cap length so a stray long
  // value can't blow out the column width.
  return trimmed.length > MAX_FALLBACK_LEN
    ? trimmed.slice(0, MAX_FALLBACK_LEN)
    : trimmed;
};
